import {
  FormControl,
  InputGroup,
  InputRightElement,
  StyleProps,
  useMergeRefs,
} from "@chakra-ui/react";
import { dataAttr, FocusableElement } from "@chakra-ui/utils";
import { ChakraStylesConfig } from "chakra-react-select/dist/types/types";
import React, { useMemo } from "react";
import { useController } from "react-hook-form";
import { SelectInstance } from "react-select";
import { usePrefixedFieldName } from "shared/components/form/FormFieldPrefix";
import { useIsFormDisabled } from "shared/components/form/FormStateProvider";
import { chakraSimpleSelectStyles } from "shared/components/input/select/ChakraSimpleSelectStyles";
import { dataTableSelectStyles } from "shared/components/input/select/DataTableSelectStyles";
import {
  SelectBaseProps,
  SelectInternal,
} from "shared/components/input/select/SelectInternal";
import { SelectOptionData } from "shared/components/input/select/SelectTypes";
import { getSelectOptionsFromChildren } from "shared/components/input/select/SelectUtils";
import { SimpleInputErrorElement } from "shared/components/input/select/SimpleInputErrorElement";
import { FormInputValue } from "shared/types/FormInputValue";
import { getFieldError } from "shared/utils/FormUtils";

export interface SimpleSelectProps<
  T extends FormInputValue = string,
  IsMulti extends boolean = false,
> extends SelectBaseProps<T, IsMulti> {
  title?: string;
  error?: string;
  children?: React.ReactNode;
  minWidth?: string;
  chakraStyles?: ChakraStylesConfig<SelectOptionData>;
  size?: "sm" | "md";
  variant?: "simple" | "simple-table";
  isSearchable?: boolean;
  controlProps?: StyleProps;
}

// This type is needed so the generic type args are not lost when using forwardRef
export type SimpleSelect = <
  T extends FormInputValue = string,
  IsMulti extends boolean = false,
>(
  props: SimpleSelectProps<T, IsMulti> & {
    ref?: React.Ref<
      SelectInstance<SelectOptionData, IsMulti> | FocusableElement
    >;
  },
) => React.ReactElement | null;

// eslint-disable-next-line no-redeclare,react/display-name
export const SimpleSelect = React.forwardRef(
  <T extends FormInputValue = string, IsMulti extends boolean = false>(
    {
      children,
      title,
      error,
      minWidth,
      variant = "simple",
      chakraStyles = variant === "simple-table"
        ? dataTableSelectStyles
        : chakraSimpleSelectStyles,
      size,
      controlProps,
      ...props
    }: SimpleSelectProps<T, IsMulti>,
    ref: React.Ref<SelectInstance<SelectOptionData, IsMulti>>,
  ) => {
    const optionsFromChildren = useMemo(
      () => getSelectOptionsFromChildren(children),
      [children],
    );
    const inputRightElement = error ? (
      <SimpleInputErrorElement error={error} />
    ) : undefined;

    return (
      <FormControl
        variant={variant}
        minWidth={minWidth}
        isInvalid={!!error}
        isDisabled={props.isDisabled}
        data-invalid={dataAttr(!!error)}
        data-disabled={dataAttr(props.isDisabled)}
        {...controlProps}
      >
        <InputGroup variant={variant} size={size}>
          <SelectInternal
            ref={ref}
            options={optionsFromChildren}
            placeholder={title}
            chakraStyles={chakraStyles}
            isInvalid={!!error}
            {...props}
          />
          {inputRightElement && (
            <InputRightElement h="100%">{inputRightElement}</InputRightElement>
          )}
        </InputGroup>
      </FormControl>
    );
  },
) as unknown as SimpleSelect;
(SimpleSelect as React.FC).displayName = "SimpleSelect";

export interface FormSimpleSelectProps extends SimpleSelectProps {
  name: string;
  shouldUnregister?: boolean;
  defaultValue?: string;
}

export const FormSimpleSelect = React.forwardRef<
  HTMLInputElement,
  FormSimpleSelectProps
>(
  (
    {
      name,
      isDisabled,
      defaultValue = "",
      shouldUnregister = true,
      ...otherProps
    },
    fwRef,
  ) => {
    const prefixedName = usePrefixedFieldName(name);
    const {
      field: { ref: fieldRef, onChange, ...field },
      formState: { errors },
    } = useController({
      name: prefixedName,
      shouldUnregister,
      defaultValue,
    });
    const error = getFieldError(errors, prefixedName)?.message;
    const mergedRef = useMergeRefs(fieldRef, fwRef);
    const isFormDisabled = useIsFormDisabled();
    return (
      <SimpleSelect
        ref={mergedRef}
        {...field}
        {...otherProps}
        isInvalid={!!error}
        isDisabled={isDisabled || isFormDisabled}
        error={error}
        onChangeValue={(value) => {
          onChange(value || "");
        }}
      />
    );
  },
);
FormSimpleSelect.displayName = "FormSimpleSelect";
